import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Loader2, Search } from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints } from '../../../styles/theme';
import { formatEventLocalDateTime } from '../../../utils/eventDateTime';

export const Workspace = styled.div`
  border: 1px solid ${colors.border.light};
  background: ${colors.white};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
`;

export const ObjectTabs = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px;
  margin-bottom: ${spacing[5]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.lg};
  background: ${colors.background.secondary};
`;

export const ObjectTab = styled.button<{ $active: boolean }>`
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borderRadius.md};
  background: ${(props) => props.$active ? colors.white : 'transparent'};
  color: ${(props) => props.$active ? colors.text.primary : colors.text.secondary};
  font-weight: ${typography.fontWeights.semibold};
  font-size: ${typography.fontSizes.sm};
  box-shadow: ${(props) => props.$active ? '0 1px 2px rgba(0, 0, 0, 0.08)' : 'none'};

  &:hover {
    color: ${colors.text.primary};
  }
`;

export const SplitPane = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 34%) minmax(0, 1fr);
  min-height: 640px;

  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const ListPane = styled.aside`
  border-right: 1px solid ${colors.border.light};
  background: ${colors.background.secondary};
  min-width: 0;

  @media (max-width: ${breakpoints.lg}) {
    border-right: 0;
    border-bottom: 1px solid ${colors.border.light};
  }
`;

export const DetailPane = styled.section`
  padding: ${spacing[5]};
  min-width: 0;

  @media (max-width: ${breakpoints.sm}) {
    padding: ${spacing[4]};
  }
`;

export const PaneToolbar = styled.div`
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.white};
`;

export const SearchRow = styled.form`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px;
  gap: ${spacing[2]};
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
`;

export const IconButton = styled.button<{ $tone?: 'default' | 'danger' }>`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => props.$tone === 'danger'
    ? colors.error[200]
    : colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${(props) => props.$tone === 'danger'
    ? colors.error[600]
    : colors.text.secondary};

  &:hover:not(:disabled) {
    background: ${(props) => props.$tone === 'danger'
      ? colors.error[50]
      : colors.background.secondary};
    color: ${(props) => props.$tone === 'danger'
      ? colors.error[700]
      : colors.text.primary};
  }
`;

export const ResultList = styled.div`
  max-height: 720px;
  overflow-y: auto;
`;

export const ResultButton = styled.button<{ $selected?: boolean }>`
  width: 100%;
  display: block;
  padding: ${spacing[4]};
  text-align: left;
  border-bottom: 1px solid ${colors.border.light};
  border-left: 3px solid ${(props) => props.$selected
    ? colors.primary[500]
    : 'transparent'};
  background: ${(props) => props.$selected ? colors.primary[50] : 'transparent'};

  &:hover {
    background: ${(props) => props.$selected ? colors.primary[50] : colors.gray[100]};
  }
`;

export const ResultTitle = styled.div`
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeights.semibold};
  font-size: ${typography.fontSizes.sm};
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

export const ResultMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
  flex-wrap: wrap;
`;

export const Badge = styled.span<{ $tone?: 'neutral' | 'success' | 'warning' | 'info' }>`
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  background: ${(props) => {
    if (props.$tone === 'success') return colors.success[50];
    if (props.$tone === 'warning') return colors.warning[50];
    if (props.$tone === 'info') return colors.primary[50];
    return colors.gray[100];
  }};
  color: ${(props) => {
    if (props.$tone === 'success') return colors.success[700];
    if (props.$tone === 'warning') return colors.warning[800];
    if (props.$tone === 'info') return colors.primary[700];
    return colors.text.secondary;
  }};
`;

export const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
  flex-wrap: wrap;
`;

export const DetailTitle = styled.h3`
  margin: 0 0 ${spacing[1]};
  font-size: ${typography.fontSizes.xl};
  overflow-wrap: anywhere;
`;

export const DetailSubtitle = styled.div`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

export const Section = styled.section`
  padding-top: ${spacing[4]};
  margin-top: ${spacing[4]};
  border-top: 1px solid ${colors.border.light};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[4]};
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: ${spacing[4]};

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const FullWidth = styled.div`
  grid-column: 1 / -1;
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 92px;
  padding: ${spacing[3]};
  resize: vertical;
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSizes.sm};
`;

export const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  accent-color: ${colors.primary[600]};
`;

export const InlineControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 40px;
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
`;

export const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
`;

export const DataTable = styled.table`
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
  font-size: ${typography.fontSizes.sm};

  th, td {
    padding: ${spacing[2]} ${spacing[3]};
    border-bottom: 1px solid ${colors.border.light};
    text-align: left;
    vertical-align: top;
  }

  th {
    background: ${colors.background.secondary};
    color: ${colors.text.secondary};
    font-size: ${typography.fontSizes.xs};
    text-transform: uppercase;
    font-weight: ${typography.fontWeights.semibold};
  }

  tr:last-child td {
    border-bottom: 0;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

export const ResourceList = styled.div`
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  overflow: hidden;
`;

export const ResourceRow = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  align-items: center;
  gap: ${spacing[4]};
  padding: ${spacing[3]};
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: ${breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${spacing[3]};
  }
`;

export const ResourceContent = styled.div`
  min-width: 0;
`;

export const ResourceTitle = styled.div`
  color: ${colors.text.primary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.semibold};
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

export const ResourceDescription = styled.div`
  margin-top: 2px;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

export const ResourceMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
`;

export const CompactActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${spacing[2]};

  @media (max-width: ${breakpoints.sm}) {
    justify-content: flex-start;
  }
`;

export const CompactActionButton = styled.button<{ $tone?: 'default' | 'danger' }>`
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  padding: 0 ${spacing[2]};
  border: 1px solid ${(props) => props.$tone === 'danger'
    ? colors.error[200]
    : colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${(props) => props.$tone === 'danger'
    ? colors.error[700]
    : colors.text.secondary};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.medium};
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
  }

  &:hover:not(:disabled) {
    background: ${(props) => props.$tone === 'danger'
      ? colors.error[50]
      : colors.background.secondary};
    color: ${(props) => props.$tone === 'danger'
      ? colors.error[800]
      : colors.text.primary};
  }
`;

export const InfoStrip = styled.dl`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${spacing[3]} ${spacing[6]};
  margin: ${spacing[3]} 0 0;
  padding: ${spacing[3]} 0;
  border-top: 1px solid ${colors.border.light};
  border-bottom: 1px solid ${colors.border.light};
`;

export const InfoItem = styled.div`
  min-width: 100px;
  max-width: 420px;

  dt {
    color: ${colors.text.tertiary};
    font-size: ${typography.fontSizes.xs};
    font-weight: ${typography.fontWeights.semibold};
    text-transform: uppercase;
  }

  dd {
    margin: 2px 0 0;
    color: ${colors.text.primary};
    font-size: ${typography.fontSizes.sm};
    line-height: 1.4;
    overflow-wrap: anywhere;
  }
`;

export const EmptyState = styled.div`
  min-height: 180px;
  padding: ${spacing[8]} ${spacing[4]};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.sm};
`;

export const ErrorBanner = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  margin-bottom: ${spacing[4]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.md};
  background: ${colors.error[50]};
  color: ${colors.error[800]};
  font-size: ${typography.fontSizes.sm};
`;

export const Hint = styled.div`
  margin-top: ${spacing[1]};
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSizes.xs};
  line-height: 1.5;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const AnimatedLoader = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
`;

export const Loading: React.FC<{ label?: string }> = ({ label = 'Loading' }) => (
  <EmptyState>
    <AnimatedLoader size={22} aria-hidden="true" />
    <span style={{ marginTop: spacing[2] }}>{label}</span>
  </EmptyState>
);

export const SearchSubmit: React.FC<{ label: string; disabled?: boolean }> = ({
  label,
  disabled,
}) => (
  <IconButton type="submit" title={label} aria-label={label} disabled={disabled}>
    <Search size={18} />
  </IconButton>
);

export function formatDate(value?: string | null): string {
  if (!value) return 'No date';
  return formatEventLocalDateTime(value) || value;
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The request could not be completed.';
}